/* eslint-disable */
// Legacy section effects have a single owner: leaving the React route disposes
// global listeners, media-query listeners, observers, frames and timers.
export function createSectionLifecycle(root) {
  const nativeWindow = window;
  const nativeDocument = document;
  const cleanups = [];
  const frames = new Set();
  const timers = new Set();
  const intervals = new Set();
  let disposed = false;
  const initialOverflow = nativeDocument.body.style.overflow;
  const listen = (target, type, listener, options) => {
    target.addEventListener(type, listener, options);
    cleanups.push(() => target.removeEventListener(type, listener, options));
  };
  const ownFrame = callback => {
    const id = nativeWindow.requestAnimationFrame(time => { frames.delete(id); if (!disposed && root.isConnected) callback(time); });
    frames.add(id); return id;
  };
  const cancelFrame = id => { frames.delete(id); nativeWindow.cancelAnimationFrame(id); };
  const ownTimeout = (callback, delay, ...args) => {
    const id = nativeWindow.setTimeout(() => { timers.delete(id); if (!disposed) callback(...args); }, delay);
    timers.add(id); return id;
  };
  const ownInterval = (callback, delay, ...args) => { const id = nativeWindow.setInterval(() => { if (!disposed) callback(...args); }, delay); intervals.add(id); return id; };
  const ownObserver = Constructor => Constructor && class extends Constructor {
    constructor(callback, options) { super((...args) => { if (!disposed) callback(...args); }, options); cleanups.push(() => this.disconnect()); }
  };
  const ownMatchMedia = query => {
    const media = nativeWindow.matchMedia(query);
    return new Proxy(media, { get(target, key) {
      if (key === 'addEventListener') return (type, listener, options) => listen(target, type, listener, options);
      if (key === 'addListener') return listener => { target.addListener(listener); cleanups.push(() => target.removeListener(listener)); };
      const value = Reflect.get(target, key, target); return typeof value === 'function' ? value.bind(target) : value;
    } });
  };
  const owned = {
    requestAnimationFrame: ownFrame, cancelAnimationFrame: cancelFrame,
    setTimeout: ownTimeout, clearTimeout: id => { timers.delete(id); nativeWindow.clearTimeout(id); },
    setInterval: ownInterval, clearInterval: id => { intervals.delete(id); nativeWindow.clearInterval(id); },
    matchMedia: ownMatchMedia,
    IntersectionObserver: ownObserver(nativeWindow.IntersectionObserver),
    ResizeObserver: ownObserver(nativeWindow.ResizeObserver),
    MutationObserver: ownObserver(nativeWindow.MutationObserver),
    addEventListener: (type, listener, options) => listen(nativeWindow, type, listener, options),
    removeEventListener: nativeWindow.removeEventListener.bind(nativeWindow),
  };
  const documentFacade = new Proxy(nativeDocument, { get(target, key) {
    if (key === 'documentElement') return root;
    if (key === 'querySelector') return root.querySelector.bind(root);
    if (key === 'querySelectorAll') return root.querySelectorAll.bind(root);
    if (key === 'getElementById') return id => root.querySelector(`[id="${CSS.escape(id)}"]`);
    if (key === 'addEventListener') return (type, listener, options) => listen(target, type, listener, options);
    if (key === 'fonts') return { ready: { then: callback => target.fonts.ready.then(() => { if (!disposed) return callback(); }) } };
    const value = Reflect.get(target, key, target); return typeof value === 'function' ? value.bind(target) : value;
  } });
  const windowFacade = new Proxy(nativeWindow, { get(target, key) {
    if (key in owned) return owned[key];
    if (key === 'document') return documentFacade;
    const value = Reflect.get(target, key, target); return typeof value === 'function' ? value.bind(target) : value;
  } });
  return {
    environment: { ...owned, document: documentFacade, window: windowFacade },
    dispose() {
      disposed = true;
      frames.forEach(id => nativeWindow.cancelAnimationFrame(id));
      timers.forEach(id => nativeWindow.clearTimeout(id));
      intervals.forEach(id => nativeWindow.clearInterval(id));
      cleanups.reverse().forEach(cleanup => cleanup());
      root.getAnimations?.({ subtree: true }).forEach(animation => animation.cancel());
      if (root.querySelector('dialog[open]')) nativeDocument.body.style.overflow = initialOverflow;
    },
  };
}
